import { toast } from 'react-toastify';

import { TamResultaat } from '@/components/dialogs/beheren/tam/TamResultaat';
import { useLoading } from '@/contextProviders/loadingProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import { toastErrorOptions, toastLoadingOptions, toastSuccessOptions } from '@/utils/toastOptions';

export interface Executable {
  url: string;
  formData: any;
  onSuccess: string;
  onError: string;
}

export function useTamUpload() {
  const { setLoading } = useLoading();
  const { fetchWithAuth } = useFetchWithAuth();

  const execute = async ({ url, formData, onSuccess, onError }: Executable): Promise<TamResultaat | null> => {
    const loadingToast = toast.loading(
      'Bezig met TAM plan laden. Ververs je venster niet! Dit kan lang duren.',
      toastLoadingOptions,
    );
    try {
      setLoading(true);

      const response = await fetchWithAuth(
        url,
        {
          method: 'POST',
          body: formData,
        },
        160_000, // 160 seconds timeout
      );

      const responseText = await response.text();
      let responseData: TamResultaat | undefined;
      if (response.status === 200 || response.status === 400) {
        try {
          responseData = JSON.parse(responseText) as TamResultaat;
          console.log(`Response voor tam uploaden: ${responseData}`);
        } catch {
          responseData = undefined;
        }
      }

      switch (true) {
        case response.status === 200:
          toast.success(onSuccess, toastSuccessOptions);
          return responseData ?? null;
        case response.status >= 500:
          toast.error(
            'Tam-plan verwerken is niet gelukt vanwege een technische fout. Probeer het nog een keer of neem contact op met support.',
            toastErrorOptions,
          );
          break;
        case response.status >= 400:
          toast.error(onError, toastErrorOptions);
          return responseData ?? null;
        default:
          toast.error('Er is een onbekende fout opgetreden. Neem contact op met support.', toastErrorOptions);
          break;
      }

      return null;
    } catch (error) {
      console.log(error);
      return null;
    } finally {
      toast.done(loadingToast);
      setLoading(false);
    }
  };

  const tamUpload = async (formData: FormData, isDryRun: boolean): Promise<TamResultaat | null> => {
    const onSuccess = isDryRun
      ? 'Het testdraaien van het TAM plan is succesvol uitgevoerd.'
      : 'Het TAM plan is succesvol toegepast.';
    const onError = isDryRun
      ? 'Het testdraaien van het TAM plan is mislukt vanwege ongeldige invoer. Controleer de bestanden op onjuistheden.'
      : 'De TAM plan verwerking is mislukt vanwege ongeldige invoer. Controleer de bestanden op onjuistheden.';
    return execute({
      url: `/tam/v1?isDryRun=${isDryRun}`,
      formData,
      onSuccess,
      onError,
    });
  };

  return {
    tamUpload,
  };
}
